import React, { memo, useMemo } from 'react';
import { View, Text, StyleSheet, Platform, Alert, type TextStyle } from 'react-native';
import { Clickable } from '../../components/common/Clickable';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { useTranslation, getLocalizedWeekdaysShort, getLocalizedWeekdaysLong } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import { androidFlatSurfaceStyle } from '../../shared/theme/studio/stackScreenShell';
import {
  buildCheckoutDayOffPickerChips,
  toggleCheckoutDayOff,
  type PlanDayOffOption,
} from '../../data/domains/checkout';

const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const FORCED_CHIP_RADIUS = 8;

type Props = {
  temp_day_off_options: PlanDayOffOption[];
  forced_day_offs: number[];
  selected_day_offs: number[];
  max_day_off: number;
  on_change: (next: number[]) => void;
};

function join_weekday_names(names: string[], locale: string): string {
  if (names.length === 0) {
    return '';
  }
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return locale.startsWith('ar') ? `${names[0]} و ${names[1]}` : `${names[0]} and ${names[1]}`;
  }
  const last = names[names.length - 1];
  const head = names.slice(0, -1).join(', ');
  return locale.startsWith('ar') ? `${head} و ${last}` : `${head} and ${last}`;
}

function format_mandatory_off_hint(
  forced_day_offs: number[],
  weekday_labels_long: string[],
  locale: string,
  t: (key: string) => string,
): string {
  const names = forced_day_offs
    .slice()
    .sort((a, b) => a - b)
    .map(day => weekday_labels_long[day])
    .filter(Boolean);
  if (names.length === 0) {
    return '';
  }
  if (names.length === 1) {
    return ` (${t('checkout.mandatoryOffSingle').replace('{{day}}', names[0])})`;
  }
  return ` (${t('checkout.mandatoryOffMultiple').replace('{{days}}', join_weekday_names(names, locale))})`;
}

function create_styles(c: ThemeColors) {
  const forced_bg = (hexToRgba(c.primary, 0.16) as string) || c.primaryUltra;
  const forced_border = (hexToRgba(c.primary, 0.42) as string) || c.primary;

  return StyleSheet.create({
    wrap: {
      marginHorizontal: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.sm,
      paddingTop: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.md,
      borderRadius: appTheme.borderRadius.lg,
      backgroundColor: hexToRgba(c.text, 0.04),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.text, 0.08),
      overflow: 'visible',
      ...androidFlatSurfaceStyle(),
    },
    title: {
      ...appTheme.typography.caption,
      fontSize: 11,
      fontWeight: '800',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      color: hexToRgba(c.text, 0.72),
      marginBottom: appTheme.spacing.sm,
      ...ANDROID_TEXT_COMPACT,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      width: '100%',
    },
    chip_slot: {
      flex: 1,
      minWidth: 0,
    },
    chip: {
      width: '100%',
      height: 36,
      paddingHorizontal: 2,
      borderRadius: appTheme.borderRadius.pill,
      backgroundColor: c.surface,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.text, 0.12),
      alignItems: 'center',
      justifyContent: 'center',
    },
    chip_selected: {
      backgroundColor: c.primary,
      borderColor: c.primary,
    },
    chip_forced: {
      backgroundColor: forced_bg,
      borderColor: forced_border,
      borderWidth: 1,
      borderRadius: FORCED_CHIP_RADIUS,
    },
    chip_disabled: {
      opacity: 0.45,
    },
    chip_text: {
      fontSize: 11,
      fontWeight: '700',
      color: c.text,
      textAlign: 'center',
      ...ANDROID_TEXT_COMPACT,
    },
    chip_text_selected: {
      color: c.onPrimary ?? c.white,
    },
    chip_text_forced: {
      color: c.primary,
      fontWeight: '800',
    },
    hint: {
      marginTop: appTheme.spacing.sm,
      fontSize: 12,
      lineHeight: 17,
      color: c.textSecondary,
      ...ANDROID_TEXT_COMPACT,
    },
  });
}

export const CheckoutDayOffWeekdayPicker = memo(function CheckoutDayOffWeekdayPicker({
  temp_day_off_options,
  forced_day_offs,
  selected_day_offs,
  max_day_off,
  on_change,
}: Props) {
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => create_styles(colors), [colors]);
  const { t, locale } = useTranslation();
  const rtl = useRtl();
  const weekday_labels = useMemo(() => getLocalizedWeekdaysShort(locale ?? 'en'), [locale]);
  const weekday_labels_long = useMemo(() => getLocalizedWeekdaysLong(locale ?? 'en'), [locale]);

  const chips = useMemo(
    () => buildCheckoutDayOffPickerChips(temp_day_off_options, forced_day_offs),
    [temp_day_off_options, forced_day_offs],
  );

  const mandatory_hint = useMemo(
    () => format_mandatory_off_hint(forced_day_offs, weekday_labels_long, locale ?? 'en', t),
    [forced_day_offs, weekday_labels_long, locale, t],
  );

  if (chips.length === 0) {
    return null;
  }

  const on_chip_press = (day: number) => {
    const { next, limit_reached } = toggleCheckoutDayOff(day, selected_day_offs, forced_day_offs, max_day_off);
    if (limit_reached) {
      Alert.alert(
        t('checkout.limitReached'),
        t('checkout.maxDayOffSelections').replace('{{count}}', String(max_day_off)),
      );
      return;
    }
    on_change(next);
  };

  return (
    <View style={styles.wrap}>
      <Text style={[styles.title, { textAlign: rtl.textAlign }]}>{t('checkout.selectAdditionalDayOff')}</Text>
      <View style={[styles.row, horizontalLtrDirectionStyle]}>
        {chips.map(chip => {
          const label = weekday_labels[chip.day] ?? String(chip.day);
          if (chip.is_forced) {
            return (
              <View
                key={chip.day}
                style={styles.chip_slot}
                accessible
                accessibilityRole="text"
                accessibilityLabel={t('checkout.mandatoryOffSingle').replace(
                  '{{day}}',
                  weekday_labels_long[chip.day] ?? label,
                )}>
                <View style={[styles.chip, styles.chip_forced]}>
                  <Text
                    style={[styles.chip_text, styles.chip_text_forced]}
                    numberOfLines={1}
                    adjustsFontSizeToFit
                    minimumFontScale={0.75}>
                    {label}
                  </Text>
                </View>
              </View>
            );
          }

          const is_selected = selected_day_offs.includes(chip.day);
          const at_limit = !is_selected && selected_day_offs.length >= max_day_off;
          return (
            <View key={chip.day} style={styles.chip_slot}>
              <Clickable
                onPress={() => on_chip_press(chip.day)}
                disabled={at_limit}
                accessibilityRole="button"
                accessibilityState={{ selected: is_selected, disabled: at_limit }}>
                <View
                  style={[
                    styles.chip,
                    is_selected && styles.chip_selected,
                    at_limit && styles.chip_disabled,
                  ]}>
                  <Text
                    style={[styles.chip_text, is_selected && styles.chip_text_selected]}
                    numberOfLines={1}
                    adjustsFontSizeToFit
                    minimumFontScale={0.75}>
                    {label}
                  </Text>
                </View>
              </Clickable>
            </View>
          );
        })}
      </View>
      {forced_day_offs.length > 0 ? (
        <Text style={[styles.hint, { textAlign: rtl.textAlign }]}>
          {t('checkout.selectUpToDayOff')}
          {max_day_off}
          {t('checkout.dayOffSuffix')}
          {mandatory_hint}
        </Text>
      ) : null}
    </View>
  );
});
